import _ from 'lodash'
import type {
    FontObject,
    FontObjectParts,
    FontPropertiesOverride,
    ParsedStyleItem,
    ParsedStyleItemPropertiesOverride,
    Property
} from '../types'
import {styleItemSelectorType} from '../parseStylableStyleItems'

const UPLOADED_FONT_REGEX = /^wfont_[0-9a-f]{6}_[0-9a-f]{32}(,\S+)*$/
const isAnUploadedFont = (fontName: string) => UPLOADED_FONT_REGEX.test(fontName)

const isThemeFont = (fontValue: string): boolean => {
    return _.isString(fontValue) && /^font_\d+$/.test(fontValue)
}

/**
 *  When font is theme-based, CSS rule should have this declaration:
 * .selector { -st-mixin: font_1 }
 **/
const isFontMixin = (property: Property): boolean =>
    property.prop === '-st-mixin' && _.isString(property.value) && property.value.includes('font_')

const FONT_PROPERTIES = ['font-style', 'font-variant', 'font-weight', 'font-size', 'font-family']
const hasFontProperties = (property: Property[]): boolean => {
    return property.some(({prop}) => FONT_PROPERTIES.includes(prop))
}

const camelize = (s: string): string => s.replace(/-./g, x => x[1].toUpperCase())

const fixLineHeightForFontShorthand = (lineHeight: string): string =>
    /\d+(\.\d+)?em/.test(lineHeight) ? lineHeight : '1.4em'

const supportedFontWeightValues = ['normal', 'bold', 'bolder', 'lighter']

const getFontWeightAsKeyword = (numberOrKeyword: string): string => {
    if (supportedFontWeightValues.includes(numberOrKeyword)) {
        return numberOrKeyword
    }
    const numericValue = parseInt(numberOrKeyword, 10)
    return numericValue >= 700 ? 'bold' : 'normal'
}

const formatPropertiesValues = (fontProperties: FontObjectParts): FontObjectParts => {
    const fontPropTransformMap: Record<string, (value: string) => string> = {
        fontWeight: getFontWeightAsKeyword
    }
    return Object.entries(fontProperties).reduce((acc, [prop, value]) => {
        const newValue = fontPropTransformMap[prop] ? fontPropTransformMap[prop](value) : value
        return {
            ...acc,
            [prop]: newValue
        }
    }, {})
}

const extractFontPropsOverrideFromRule = (rule: Property[]): FontPropertiesOverride => {
    const applicableOverrides = rule.reduce((overrides, decl) => {
        const {prop, value} = decl

        // Font overrides written above the -st-mixin are overridden by it
        if (prop === '-st-mixin') {
            return {}
        }

        if (!FONT_PROPERTIES.includes(prop)) {
            return overrides
        }

        const formattedKey = camelize(prop)

        return {...overrides, [formattedKey]: value}
    }, {})
    return formatPropertiesValues(applicableOverrides)
}

/** Turns font object into single font shorthand value (compatible with Thunderbolt validation) */
const stringifyFont = (fontObject: FontObject): string => {
    const {fontStyle, fontVariant, fontWeight, fontSize, fontSizeUnit, lineHeight, fontFamily} = fontObject
    const optionalLineHeight: string = lineHeight ? `/${lineHeight}` : ''
    const font: string = [
        fontStyle,
        fontVariant,
        fontWeight,
        `${fontSize}${fontSizeUnit}${optionalLineHeight}`,
        fontFamily
    ]
        .filter(Boolean)
        .join(' ')
    return font
}

const extractOverrides = (properties: Property[]): ParsedStyleItemPropertiesOverride => {
    return properties.reduce((acc, prop) => {
        if (!_.isEmpty(prop.override)) {
            return {
                ...acc,
                [prop.prop]: prop.override
            }
        }
        return acc
    }, {})
}

/** returns complete font object; missing properties are filled with fallback values */
const getFontPropertiesWithFallbacks = (fontProps: FontObjectParts): FontObject => {
    const {
        fontStyle = 'normal',
        fontVariant = 'normal',
        fontWeight = 'normal',
        fontSize = '16px',
        lineHeight = '1.4em',
        fontFamily = 'sans-serif'
    } = fontProps
    const sizeValue = fontSize.replace(/[a-z|%]+/, '') || '16'
    const sizeUnit = fontSize.replace(sizeValue, '') || 'px'

    return {
        fontStyle,
        fontVariant,
        fontWeight,
        fontSize: sizeValue,
        fontSizeUnit: sizeUnit,
        lineHeight: fixLineHeightForFontShorthand(lineHeight),
        fontFamily
    }
}

/** removes font-related properties (font-size, font-weight etc.) */
const filterOutFontProperties = (rule: Property[]): Property[] => {
    return rule.filter(({prop}) => ![...FONT_PROPERTIES, '-st-mixin'].includes(prop))
}

const createFontProperty = ({
    value,
    valueRaw,
    override
}: Pick<Property, 'value' | 'valueRaw' | 'override'>): Property => {
    return {
        prop: 'font',
        cssProp: 'font',
        value,
        ...(override && {override}),
        ...(valueRaw && {valueRaw})
    }
}

const pickOwnFontProperties = (properties: Property[]): FontObjectParts => {
    const cssFontProps = ['font-style', 'font-variant', 'font-weight', 'font-size', 'line-height', 'font-family']
    return cssFontProps.reduce((acc, key) => {
        const value = _.get(
            properties.find(({prop}) => prop === key),
            ['value']
        )
        const isPropDefined = value !== undefined
        return {
            ...acc,
            ...(isPropDefined && {[camelize(key)]: value})
        }
    }, {})
}

/**
 * Transforms Stylable format of font-family into valid font override format
 * Stylable font families have font fallbacks, and quotes if they contain spaces
 * Fallbacks are not needed because the Viewer adds them when rendering
 * Quotes are replaced with plus (+) singes, like here https://github.com/wix-private/document-management/blob/master/santa-ds-libs/src/coreUtils/core/fontUtils.ts#L72
 */
const removeFontFamilyFallbacks = (override: FontPropertiesOverride): FontPropertiesOverride => {
    const removeFallbacks = (str: string): string => str.split(',')[0]
    const spaceToPlusSign = (str: string): string => str.replace(/\s/g, '+')
    const removeQuotes = (str: string): string => str.replace(/"/g, '')

    const fontFamily = override?.fontFamily
    if (fontFamily && !isAnUploadedFont(fontFamily)) {
        const fontFamilyWithNoFallbacks = removeFallbacks(fontFamily)
        const validFontFamily = _.flow([spaceToPlusSign, removeQuotes])(fontFamilyWithNoFallbacks)
        return {...override, fontFamily: validFontFamily}
    }
    return override
}

const transformFontValues = (properties: Property[]): Property[] => {
    const fontMixinProperty = properties.find(isFontMixin)
    if (fontMixinProperty) {
        const override = extractFontPropsOverrideFromRule(properties)
        const fixedOverride = removeFontFamilyFallbacks(override)

        return [
            ...filterOutFontProperties(properties),
            createFontProperty({
                value: fontMixinProperty.value,
                override: fixedOverride
            })
        ]
    } else if (!hasFontProperties(properties)) {
        return properties
    }
    const ownFontProperties = pickOwnFontProperties(properties)
    const fontWithFallbacks = getFontPropertiesWithFallbacks(ownFontProperties)

    return [
        ...filterOutFontProperties(properties),
        createFontProperty({
            value: stringifyFont(fontWithFallbacks),
            valueRaw: ownFontProperties // this value will be used for state inheritance, so it's important to only keep selector's own properties without fallback values
        })
    ]
}

const hasOverriddenProperties = (source: FontObjectParts, objectWithOverrides: FontObjectParts): boolean => {
    return !Object.entries(objectWithOverrides).every(
        ([property, val]) => source[property as keyof FontObjectParts] === val
    )
}

const isFontProperty = ({prop}: Property): boolean => {
    return prop === 'font'
}

const removeFontProperty = (properties: Property[]): Property[] => {
    return properties.filter(({prop}) => prop !== 'font')
}

const replaceFontProperty = (properties: Property[], optionalNewFont: Property | null): Property[] => {
    return [...removeFontProperty(properties), ...(optionalNewFont ? [optionalNewFont] : [])]
}

const createOverrideWithInheritance = (fontProperties: FontObjectParts): FontPropertiesOverride => {
    const overrideKeys: (keyof FontObjectParts)[] = ['fontStyle', 'fontWeight', 'fontVariant', 'fontSize', 'fontFamily']
    const override = overrideKeys.reduce((acc, nextKey) => {
        const value = fontProperties[nextKey]
        if (value === undefined) {
            return acc
        }
        return {
            ...acc,
            [nextKey]: value
        }
    }, {})
    return formatPropertiesValues(override)
}

const getStateFontAfterInheritance = (rootFont: Property, stateFont: Property): Property | null => {
    if (isThemeFont(rootFont.value)) {
        const stateFontOverride = stateFont.valueRaw
        const hasOverridesOverRoot =
            rootFont.override && stateFontOverride && hasOverriddenProperties(rootFont.override, stateFontOverride)
        if (hasOverridesOverRoot) {
            const override = createOverrideWithInheritance({
                ...rootFont.override,
                ...stateFontOverride
            })
            return createFontProperty({
                value: rootFont.value,
                override
            })
        }
    } else if (
        rootFont.valueRaw &&
        stateFont.valueRaw &&
        hasOverriddenProperties(rootFont.valueRaw, stateFont.valueRaw)
    ) {
        const fontParts = {...rootFont.valueRaw, ...stateFont.valueRaw}
        return createFontProperty({
            value: stringifyFont(getFontPropertiesWithFallbacks(fontParts)),
            valueRaw: fontParts
        })
    }
    return null
}

const inheritFontFromRoot = (stateProps: Property[], rootProps: Property[]): Property[] => {
    const stateFont = stateProps.find(isFontProperty)
    const rootFont = rootProps.find(isFontProperty)
    const shouldNotInherit = !stateFont || !rootFont || isThemeFont(stateFont.value)
    if (shouldNotInherit) {
        return stateProps
    }
    const optionalNewFont = rootFont && stateFont ? getStateFontAfterInheritance(rootFont, stateFont) : null
    return replaceFontProperty(stateProps, optionalNewFont)
}

const migrateFonts = (styleItems: ParsedStyleItem[]): ParsedStyleItem[] => {
    return styleItems.map(styleItem => {
        const {parsedStyle} = styleItem

        const updatedParsedStyle = Object.entries(parsedStyle).reduce(
            (acc, [selector, {properties, mobileProperties}]) => {
                const updatedProperties = transformFontValues(properties)
                const updatedMobileProperties = transformFontValues(mobileProperties)
                return {
                    ...acc,
                    [selector]: {
                        ...acc[selector],
                        properties: updatedProperties,
                        mobileProperties: updatedMobileProperties,
                        propertiesOverride: extractOverrides(updatedProperties),
                        mobilePropertiesOverride: extractOverrides(updatedMobileProperties)
                    }
                }
            },
            parsedStyle
        )
        const parsedStyleWithStateInheritance = Object.entries(updatedParsedStyle).reduce(
            (acc, [selector, {properties, mobileProperties, metadata}]) => {
                const isStateStyle = metadata.type === styleItemSelectorType.STATE
                const rootSelectorStyle = metadata.root && updatedParsedStyle[metadata.root]
                if (!isStateStyle || !rootSelectorStyle) {
                    return acc
                }
                const updatedProperties = inheritFontFromRoot(properties, rootSelectorStyle.properties)
                const updatedMobileProperties = inheritFontFromRoot(
                    mobileProperties,
                    rootSelectorStyle.mobileProperties
                )
                return {
                    ...acc,
                    [selector]: {
                        ...acc[selector],
                        properties: updatedProperties,
                        mobileProperties: updatedMobileProperties,
                        propertiesOverride: extractOverrides(updatedProperties),
                        mobilePropertiesOverride: extractOverrides(updatedMobileProperties)
                    }
                }
            },
            updatedParsedStyle
        )

        return {
            ...styleItem,
            parsedStyle: parsedStyleWithStateInheritance
        }
    })
}

export default migrateFonts
